import { RefObject, useEffect } from 'react';
import { CloseButton } from '@mantine/core';
import { HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { useDataTableFind } from '../useDataTableFind';

type FindOptions = Parameters<typeof useDataTableFind>[0];

interface IDataTableFindProps extends Pick<FindOptions, 'rows' | 'colIds' | 'rowsRef' | 'reveal'> {
    inputRef: RefObject<HTMLInputElement | null>;
    /** Запрос, переживающий поле: прежний запрос возвращается при следующем входе в полноэкранный режим */
    queryRef: RefObject<string>;
}

/**
 * «Найти в таблице» полноэкранного режима: ведёт по совпадениям, строк не прячет. Запрос — состояние поля: ввод не
 * перерисовывает таблицу.
 */
const DataTableFind = ({ inputRef, queryRef, ...options }: IDataTableFindProps) => {
    const {
        query,
        setQuery,
        clear,
        step: onStep,
        count,
        position,
        pending,
    } = useDataTableFind({ defaultQuery: queryRef.current, ...options });
    useEffect(() => {
        queryRef.current = query;
    }, [query, queryRef]);
    const onClear = () => {
        clear();
        inputRef.current?.focus();
    };

    return (
        <div className="dtb__search dtb__find" data-testid="dtb-find" data-filled={query !== '' || undefined}>
            <HiOutlineMagnifyingGlass aria-hidden />
            <input
                ref={inputRef}
                value={query}
                placeholder="Найти в таблице"
                aria-label="Найти в таблице"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onStep(e.shiftKey ? -1 : 1);
                    } else if (e.key === 'Escape' && query !== '') {
                        // первый Esc чистит запрос, режим закрывает следующий
                        e.stopPropagation();
                        onClear();
                    }
                }}
            />
            {query !== '' && (
                <div className="dtb__find-meta">
                    <span className="dtb__find-count" role="status">
                        {query.trim() === ''
                            ? ''
                            : pending
                              ? '…'
                              : count
                                ? `${position} из ${count}`
                                : 'Нет совпадений'}
                    </span>
                    <button
                        type="button"
                        className="dtb__find-step"
                        aria-label="Предыдущее совпадение"
                        title="Предыдущее совпадение (Shift+Enter)"
                        disabled={!count}
                        onClick={() => onStep(-1)}
                    >
                        <HiOutlineChevronUp aria-hidden />
                    </button>
                    <button
                        type="button"
                        className="dtb__find-step"
                        aria-label="Следующее совпадение"
                        title="Следующее совпадение (Enter)"
                        disabled={!count}
                        onClick={() => onStep(1)}
                    >
                        <HiOutlineChevronDown aria-hidden />
                    </button>
                    <CloseButton size="sm" aria-label="Очистить поиск" title="Очистить поиск (Esc)" onClick={onClear} />
                </div>
            )}
        </div>
    );
};

export default DataTableFind;
